import { IconX } from '@tabler/icons-react';
import { motion, useReducedMotion } from 'motion/react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { DUR, EASE, PRESS } from '../../interaction';

export type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'error';
export type BadgeVariant = 'subtle' | 'solid';
export type BadgeShape = 'pill' | 'rounded';
export type BadgeSize = 'sm' | 'md';

/**
 * `subtle` pairs each `--{tone}-surface` fill with its own
 * `--{tone}-on-surface` text token — the pair `tokens.css` records as measured
 * AA or better on both themes, so no ratio here is invented.
 */
const SUBTLE: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-surface text-neutral-on border-neutral',
  info: 'bg-info-surface text-info-on border-info',
  success: 'bg-success-surface text-success-on border-success',
  warning: 'bg-warning-surface text-warning-on border-warning',
  error: 'bg-danger-surface text-danger-on border-danger',
};

/**
 * `solid` fills with `--{tone}` and sets the text in `--text-on-primary`.
 * That token is white on the light theme's dark semantic fills and charcoal on
 * the dark theme's bright ones, which is the only pairing that clears 4.5:1 on
 * both — computed against `--warning` (#8a6100 light) it reads 5.0:1, the
 * tightest of the five.
 */
const SOLID: Record<BadgeTone, string> = {
  neutral: 'bg-neutral text-on-primary border-neutral',
  info: 'bg-info text-on-primary border-info',
  success: 'bg-success text-on-primary border-success',
  warning: 'bg-warning text-on-primary border-warning',
  error: 'bg-danger text-on-primary border-danger',
};

const DOT: Record<BadgeTone, string> = {
  neutral: 'bg-neutral',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-danger',
};

/**
 * Every size owns its whole scale, the font size included. Leaving the text
 * size out let the chip inherit whatever the container set — the same `sm`
 * badge measured 24px tall and 78.1px wide inside a 16px container and 63.1px
 * wide inside a 12px one, so a badge changed size because of where it was
 * placed rather than because of its own `size` prop.
 */
const SIZES: Record<BadgeSize, string> = {
  sm: 'min-h-6 gap-1 px-2 text-2xs',
  md: 'min-h-7 gap-1.5 px-2.5 text-xs',
};

/**
 * Counter geometry: a number riding on another control is a different thing
 * from a status chip, so it brings its own 18px box, a tight inset and tabular
 * figures. `h-4.5` rather than a `min-h` because the inner line box is taller
 * than the text: `text-2xs` carries its own line-height (16.5px at 11px), and
 * the chip's `leading-none` loses that cascade, so a `min-h` counter measured
 * 18.5px. The chip's letter-spacing is overridden back to `tracking-normal`
 * because tracking spaces digits apart and widens the chip for no gain. `min-w`
 * matches the height, which is what makes a single digit render as a dot.
 */
const COUNTER = 'h-4.5 min-w-4.5 justify-center gap-0 px-1 text-2xs tracking-normal';

const GLYPH: Record<BadgeSize, string> = {
  sm: 'size-3',
  md: 'size-3.5',
};

/**
 * Leading inset and child gap for the select control once a delete control
 * shares the chip: the select control then owns the inset the chip would
 * otherwise carry, and `SIZES` keeps the same pair for the single-control chip.
 */
const SELECT_INSET: Record<BadgeSize, string> = {
  sm: 'gap-1 pr-0 pl-2',
  md: 'gap-1.5 pr-0 pl-2.5',
};

export interface BadgeProps extends Omit<ComponentPropsWithRef<'span'>, 'children'> {
  /** Visible label. Optional — a dot-only or count-only badge is valid. */
  children?: ReactNode;
  /** Renders a numeric chip beside the label. */
  count?: number;
  tone?: BadgeTone;
  variant?: BadgeVariant;
  size?: BadgeSize;
  /**
   * Square (`rounded-xs`) by default, which is what the Constructivist chip
   * contract says: "square corners, so a badge reads as stamped metal rather
   * than as a soft pill" — and `CONVENTIONS.md`: "`rounded-xs` at most". A pill
   * is opt-in for the one case it earns, a chip that must read as a token rather
   * than as a label.
   */
  shape?: BadgeShape;
  /**
   * A compact counter — an 18px tile for a bare number that rides on another
   * control (a bell, a tab) rather than a content chip. Tone and variant still
   * choose the fill; `size` no longer applies.
   */
  counter?: boolean;
  /** Leading status dot, for a badge with no label. */
  dot?: boolean;
  icon?: ReactNode;
  /** Renders the chip as a real toggle button (`aria-pressed`). */
  selectable?: boolean;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  /** Adds a delete affordance. Needs `onDelete` to do anything. */
  dismissible?: boolean;
  onDelete?: () => void;
  /** Accessible name for the delete button. Defaults to "Remove {label}". */
  deleteLabel?: string;
}

/**
 * Constructivist chip: condensed uppercase label, a hard-edged tone border and
 * square corners, so a badge reads as stamped metal rather than as a soft pill.
 *
 * It is one component with three behaviours:
 * - display (default) — a static `span`;
 * - `dismissible` — the same chip with a real delete `button` inside;
 * - `selectable` — the chip itself becomes a real toggle `button`.
 *
 * The outer element is always the `span` that receives `...rest`, because the
 * three roots do not share an element type; a consumer's `id`, `style` and
 * `data-*` therefore land somewhere predictable in every mode.
 */
export function Badge({
  children,
  count,
  tone = 'neutral',
  variant = 'subtle',
  shape = 'rounded',
  size = 'md',
  counter = false,
  dot = false,
  icon,
  selectable = false,
  selected = false,
  onSelectedChange,
  dismissible = false,
  onDelete,
  deleteLabel,
  className,
  ...rest
}: BadgeProps) {
  const reduceMotion = useReducedMotion();
  const solid = variant === 'solid';

  // A counter is a tile a number rides on — a bell's unread chip — not a content
  // chip, so it keeps the circular geometry whatever `shape` says: its own count
  // bubble is `rounded-pill`, and a square chip behind a round bubble shows four
  // red corners poking out from under it. Every other badge follows `shape`,
  // which now defaults to the square the contract asks for.
  const radius = counter || shape === 'pill' ? 'rounded-pill' : 'rounded-xs';

  const chip = cn(
    'inline-flex shrink-0 items-center border font-display font-semibold uppercase leading-none tracking-label whitespace-nowrap',
    radius,
    // A counter replaces the size scale rather than layering on top of it: the
    // scale's `min-h` is a different property from the counter's `height`, so
    // both would apply and the taller one would win.
    counter ? COUNTER : SIZES[size],
  );

  const content = (
    <>
      {dot ? (
        <span
          aria-hidden="true"
          className={cn('size-1.5 shrink-0 rounded-pill', solid ? 'bg-on-primary' : DOT[tone])}
        />
      ) : null}

      {icon ? (
        // `[&>svg]:size-full` is the house pattern: the slot is already sized,
        // and without a rule on the glyph itself a Tabler icon keeps its default
        // 24px box inside a 14px slot and overflows it by ~10px.
        <span
          aria-hidden="true"
          className={cn('grid shrink-0 place-items-center [&>svg]:size-full', GLYPH[size])}
        >
          {icon}
        </span>
      ) : null}

      {children ? <span className="truncate">{children}</span> : null}

      {typeof count === 'number' ? (
        <span
          className={cn(
            'grid min-w-4 shrink-0 place-items-center rounded-pill px-1 py-0.5 text-2xs leading-none',
            solid ? 'bg-on-primary text-body' : cn('text-on-primary', DOT[tone]),
          )}
        >
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </>
  );

  // The delete control is never a child of the select control: a `<button>`
  // inside a `<button>` is invalid HTML and leaves the delete affordance
  // unreachable to a keyboard and to assistive technology. It is their sibling,
  // or the chip's own trailing control when the chip is not selectable.
  const remove = dismissible ? (
    <button
      type="button"
      onClick={onDelete}
      aria-label={deleteLabel ?? (typeof children === 'string' ? `Remove ${children}` : 'Remove')}
      className={cn(
        'dhd-target relative -mr-1 grid size-3.5 shrink-0 place-items-center rounded-pill',
        'opacity-70 transition-opacity duration-[var(--dur-fast)] hover:opacity-100',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
      )}
    >
      <IconX aria-hidden="true" className="size-3" stroke={2.5} />
    </button>
  ) : null;

  if (selectable) {
    if (!dismissible) {
      return (
        <span {...rest} className={cn('inline-flex align-middle', className)}>
          <button
            type="button"
            aria-pressed={selected}
            onClick={() => onSelectedChange?.(!selected)}
            className={cn(
              chip,
              'dhd-target relative cursor-pointer transition-colors duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
              PRESS,
              selected ? SOLID[tone] : SUBTLE[tone],
              selected ? 'hover:brightness-110' : 'hover:border-heading',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
            )}
          >
            {content}
          </button>
        </span>
      );
    }

    // Selectable and dismissible at once: the chip is a container holding two
    // sibling controls. The select control carries the chip's leading inset and
    // its own children's gap, because the chip's padding now belongs to the
    // controls; visually the chip is unchanged.
    return (
      <span {...rest} className={cn('inline-flex align-middle', className)}>
        <span
          className={cn(
            chip,
            'pl-0',
            selected ? SOLID[tone] : SUBTLE[tone],
            'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
            selected ? 'hover:brightness-110' : 'hover:border-heading',
          )}
        >
          <button
            type="button"
            aria-pressed={selected}
            onClick={() => onSelectedChange?.(!selected)}
            className={cn(
              // `uppercase` is repeated here because it does not inherit
              // through a `<button>`: the UA stylesheet sets
              // `text-transform: none` on one, so the chip's own class stops at
              // the container and the label would render as typed.
              'inline-flex cursor-pointer items-center self-stretch uppercase',
              radius,
              SELECT_INSET[size],
              PRESS,
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
            )}
          >
            {content}
          </button>
          {remove}
        </span>
      </span>
    );
  }

  return (
    <span {...rest} className={cn('inline-flex align-middle', className)}>
      <motion.span
        // Entrance only. CONVENTIONS §8 forbids forcing motion onto a badge;
        // a chip arriving with the row it belongs to is its natural state.
        initial={reduceMotion ? false : { opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={reduceMotion ? { duration: 0 } : { duration: DUR.base, ease: EASE.overshoot }}
        className={cn(chip, solid ? SOLID[tone] : SUBTLE[tone])}
      >
        {content}
        {remove}
      </motion.span>
    </span>
  );
}
